import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Footer from "../components/Footer";

const styles = (
  <style>{`
    * { box-sizing: border-box; }

    .av-page {
      position: relative;
      min-height: 100vh;
      background: #f8f9fa;
      color: #111827;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }

    /* Error state back button */
    .av-back {
      background: none;
      border: none;
      color: #6b7280;
      font-size: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border-radius: 8px;
    }
    .av-back:hover { background: #f3f4f6; }

    /* Article container — centered with thick side margins on large screens */
    .av-container {
      max-width: 800px;
      margin: 0 auto;
      padding: 48px clamp(20px, 6vw, 96px) 80px;
    }
    @media (max-width: 600px) { .av-container { padding: 24px 16px 60px; } }

    /* Ad rail — floats in the right whitespace without pushing the article */
    .av-ad-rail {
      display: none;
      position: absolute;
      left: calc(50% + 432px - clamp(20px, 6vw, 96px));
      right: 0;
      top: 80px;
      bottom: 0;
      padding: 0 12px;
    }
    @media (min-width: 1200px) {
      .av-ad-rail { display: flex; justify-content: center; align-items: flex-start; }
    }
    .av-ad-slot {
      width: 100%;
      max-width: 360px;
      aspect-ratio: 16 / 9;
      background: #fff;
      border-radius: 14px;
      border: 2px dashed rgba(249, 115, 22, 0.25);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      text-decoration: none;
      position: sticky;
      top: 130px;
      transition: border-color 0.15s, background 0.15s;
    }
    .av-ad-slot:hover {
      border-color: rgba(249, 115, 22, 0.5);
      background: #fffbf7;
    }

    /* Header image — inside the container with rounded corners */
    .av-hero {
      width: 100%;
      max-height: 460px;
      object-fit: cover;
      border-radius: 18px;
      display: block;
      margin-bottom: 32px;
    }
    @media (max-width: 600px) { .av-hero { max-height: 220px; border-radius: 12px; margin-bottom: 24px; } }

    /* Title */
    .av-title {
      font-size: clamp(1.7rem, 5vw, 2.5rem);
      font-weight: 900;
      line-height: 1.18;
      color: #111827;
      margin: 0 0 20px;
    }

    /* Meta row */
    .av-meta {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
      font-size: 13px;
      color: #6b7280;
      padding-bottom: 16px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 24px;
    }
    .av-author {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 700;
      color: #374151;
    }
    .av-author-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f97316, #ef4444);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 800;
      font-size: 13px;
      flex-shrink: 0;
    }

    /* Share */
    .av-share-menu {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 32px;
    }
    .share-btn {
      border: none;
      cursor: pointer;
      padding: 7px 14px;
      border-radius: 9999px;
      font-size: 12px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: opacity 0.15s;
      text-decoration: none;
    }
    .share-btn:hover { opacity: 0.85; }
    .share-wa   { background: #25D366; color: #fff; }
    .share-x    { background: #000; color: #fff; }
    .share-fb   { background: #1877F2; color: #fff; }
    .share-copy { background: #f3f4f6; color: #374151; }
    .share-copy.copied { background: #dcfce7; color: #15803d; }

    /* Content typography */
    .av-content h1 { font-size: clamp(1.4rem, 4vw, 1.9rem); font-weight: 800; margin: 2rem 0 0.75rem; line-height: 1.25; }
    .av-content h2 { font-size: clamp(1.2rem, 3.5vw, 1.5rem); font-weight: 700; margin: 1.75rem 0 0.6rem; }
    .av-content h3 { font-size: clamp(1rem, 3vw, 1.2rem); font-weight: 700; margin: 1.5rem 0 0.5rem; }
    .av-content p  { font-size: clamp(15px, 3vw, 17px); line-height: 1.85; color: #374151; margin-bottom: 1.1rem; }
    .av-content img {
      max-width: 100%;
      border-radius: 12px;
      margin: 1rem 0;
      display: block;
      box-shadow: 0 2px 16px rgba(0,0,0,0.08);
    }
    .av-content figure { margin: 1rem 0; }
    .av-content figure img { margin: 0; width: 100%; }
    .av-content figcaption {
      text-align: center;
      font-size: 13px;
      color: #9ca3af;
      font-style: italic;
      margin-top: 10px;
    }
    .av-content blockquote {
      border-left: 4px solid #f97316;
      padding: 12px 20px;
      background: #fff7f3;
      border-radius: 0 10px 10px 0;
      margin: 1.5rem 0;
      font-style: italic;
      color: #374151;
      font-size: 16px;
    }
    .av-content code {
      background: #f3f4f6;
      padding: 2px 7px;
      border-radius: 4px;
      font-size: 14px;
      font-family: 'Courier New', monospace;
    }
    .av-content pre {
      background: #1e293b;
      color: #e2e8f0;
      padding: 20px;
      border-radius: 12px;
      overflow-x: auto;
      margin: 1rem 0;
    }
    .av-content pre code { background: transparent; color: inherit; padding: 0; }
    .av-content ul, .av-content ol { padding-left: 1.5rem; margin-bottom: 1rem; }
    .av-content li { font-size: clamp(15px, 3vw, 17px); line-height: 1.8; color: #374151; margin-bottom: 6px; }
    .av-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 2rem 0; }
    .av-content a { color: #f97316; text-decoration: underline; text-underline-offset: 3px; }
    .av-content strong { font-weight: 700; color: #111827; }

    /* Loading / error */
    .av-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 60vh;
      color: #9ca3af;
      font-size: 15px;
      gap: 10px;
    }
  `}</style>
);

function getInitials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function formatDate(dt) {
  if (!dt) return "";
  return new Date(dt).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const ArticleView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await fetch(`/api/articles/${id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setArticle({
          ...data,
          content: data.resolvedContent || data.content || "",
          headerImage: data.headerImageUrl || "",
        });
      } catch (e) {
        console.error(e);
        setErr("Article not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = article ? encodeURIComponent(`${article.title} — via BLEEP`) : "";
  const shareUrl = encodeURIComponent(pageUrl);

  const handleCopy = () => {
    navigator.clipboard.writeText(pageUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      {article && (
        <Helmet>
          <title>{article.title} | BLEEP</title>
          <meta name="description" content={article.content?.substring(0, 160)} />
          {article.headerImage && <meta property="og:image" content={article.headerImage} />}
        </Helmet>
      )}

      {styles}

      <div className="av-page">
        {loading && (
          <div className="av-loading">
            <i className="fas fa-spinner fa-spin" />
            Loading article…
          </div>
        )}

        {!loading && err && (
          <div className="av-loading" style={{ flexDirection: "column", gap: 12 }}>
            <i className="fas fa-exclamation-circle" style={{ fontSize: 32, color: "#f97316" }} />
            <span>{err}</span>
            <button className="av-back" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left" /> Go back
            </button>
          </div>
        )}

        {!loading && article && (
          <>
          <div className="av-container">
            {/* Title */}
            <h1 className="av-title">{article.title}</h1>

            {/* Header image */}
            {article.headerImage && (
              <img
                src={article.headerImage}
                alt={article.title}
                className="av-hero"
              />
            )}

            {/* Meta */}
            <div className="av-meta">
              {article.author && (
                <div className="av-author">
                  <div className="av-author-avatar">{getInitials(article.author)}</div>
                  {article.author}
                </div>
              )}
              {article.dateTime && (
                <span>
                  <i className="fas fa-calendar me-1" />
                  {formatDate(article.dateTime)}
                </span>
              )}
            </div>

            {/* Share */}
            <div className="av-share-menu">
              <a
                href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn share-wa"
              >
                <i className="fab fa-whatsapp" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn share-x"
              >
                <i className="fab fa-x-twitter" />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn share-fb"
              >
                <i className="fab fa-facebook" />
              </a>
              <button
                className={`share-btn share-copy ${copied ? "copied" : ""}`}
                onClick={handleCopy}
              >
                <i className={`fas ${copied ? "fa-check" : "fa-link"}`} />
                {copied ? "Copied!" : "Copy link"}
              </button>
            </div>

            {/* Content */}
            <div className="av-content">
              <ReactMarkdown
                components={{
                  img: ({ src, alt }) => (
                    <figure>
                      <img src={src} alt={alt} />
                      {alt && <figcaption>{alt}</figcaption>}
                    </figure>
                  ),
                }}
              >
                {article.content || ""}
              </ReactMarkdown>
            </div>
          </div>
          <aside className="av-ad-rail" aria-label="Advertisement">
            <Link to="/contact" className="av-ad-slot">
              <i className="fas fa-bullhorn" style={{ fontSize: 26, color: "#d1d5db", display: "block", marginBottom: 10 }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: "#9ca3af" }}>Your Ad Here</div>
              <div style={{ fontSize: 11, color: "#b0b6c0", marginTop: 3 }}>Reach our community</div>
            </Link>
          </aside>
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ArticleView;
